import React, { useState, useEffect } from 'react';
import { Box, Stack, Title, Text, Textarea, Button, Notification, Group, Card, SegmentedControl, Badge, Paper, Divider, Flex } from '@mantine/core';
import { IconBug, IconCheck, IconX, IconList, IconRefresh, IconRobot, IconTrash } from '@tabler/icons-react';

export default function BugReportTab({ user, apiBaseUrl, t }) {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [viewMode, setViewMode] = useState('submit');
  const [tickets, setTickets] = useState([]);
  const [fetching, setFetching] = useState(false);

  const isAdmin = user?.role?.includes('ADMIN') || user?.email === 'office@ailinkb.com';

  const fetchTickets = () => {
    if (!user?.email) return;
    setFetching(true);
    fetch(`${apiBaseUrl}/api/bugs?reporterEmail=${user.email}&isAdmin=${isAdmin}`)
      .then(r => r.json())
      .then(data => {
        setFetching(false);
        if (Array.isArray(data)) setTickets(data);
      })
      .catch(e => {
        setFetching(false);
        console.error("Error fetching bugs", e);
      });
  };

  useEffect(() => {
    fetchTickets();
  }, [user, isAdmin]);

  const handleSubmit = async () => {
    if (!description.trim() || !user?.email) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/bugs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, reporterEmail: user.email })
      });
      if (res.ok) {
        setStatus('success');
        setDescription('');
        fetchTickets();
      } else {
        await fetch(`${apiBaseUrl}/api/reports/bug`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description, ownerEmail: user.email })
        });
        setStatus('success');
        setDescription('');
        fetchTickets();
      }
    } catch (e) {
      console.error(e);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (ticketId, newStatus) => {
    fetch(`${apiBaseUrl}/api/bugs/${ticketId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus, adminEmail: user?.email })
    })
    .then(r => r.json())
    .then(() => {
      fetchTickets();
      alert(t('bug_status_updated_msg', 'Status błędu został zaktualizowany! Jeśli ustawiono na Rozwiązany (RESOLVED), klient otrzymał powiadomienie e-mail (EN).'));
    })
    .catch(e => alert(e.message));
  };

  const handleDeleteTicket = (id) => {
    if (!window.confirm("Na pewno usunąć to zgłoszenie?")) return;
    fetch(`${apiBaseUrl}/api/bugs/${id}`, { method: 'DELETE' })
      .then(() => fetchTickets())
      .catch(e => alert(e.message));
  };

  return (
    <Box p="md" h="100%">
      <Card withBorder shadow="sm" radius="md" p="xl">
        <Group justify="space-between" mb="lg">
          <Group gap="sm">
            <IconBug size={28} color="red" />
            <Title order={3}>{t('tab_report_bug', 'Zgłoś Błąd')}</Title>
          </Group>
          {isAdmin && (
            <Badge color="red" size="lg" variant="filled">
              ADMIN TRIAGE MODE (ALL ISSUES)
            </Badge>
          )}
        </Group>

        <SegmentedControl
          value={viewMode}
          onChange={setViewMode}
          mb="xl"
          size="sm"
          data={[
            { value: 'submit', label: <Group gap="xs"><IconBug size={16}/><span>{t('bug_tab_submit', '📝 Zgłoś Nowy Problem')}</span></Group> },
            { value: 'list', label: <Group gap="xs"><IconList size={16}/><span>{isAdmin ? t('bug_tab_history_admin', '📋 Wszystkie Zgłoszenia Błędów (Panel Admina & AI Triage)') : t('bug_tab_history', '📋 Moje Zgłoszenia i Status Pracy')} ({tickets.length})</span></Group> },
          ]}
        />

        {viewMode === 'submit' && (
          <Stack gap="md" maw={680}>
            <Text size="sm" c="dimmed">
              {t('report_bug_desc')}
            </Text>

            <Textarea
              label={t('report_bug_label')}
              placeholder={t('report_bug_placeholder')}
              minRows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />

            <Button onClick={handleSubmit} loading={loading} color="red" mt="md" size="md" fullWidth>
              {t('report_bug_submit')}
            </Button>

            {status === 'success' && (
              <Notification icon={<IconCheck size={18} />} color="teal" title={t('report_bug_success_title')} onClose={() => setStatus(null)}>
                {t('report_bug_success_desc', 'Dziękujemy! Twój błąd został zewidencjonowany w naszym systemie i przejęty przez inżynierów AI.')}
                <Box mt="xs">
                  <Button variant="outline" color="teal" size="xs" onClick={() => setViewMode('list')}>
                    Przejdź do listy zgłoszonych problemów ➔
                  </Button>
                </Box>
              </Notification>
            )}

            {status === 'error' && (
              <Notification icon={<IconX size={18} />} color="red" title={t('report_bug_error_title')} onClose={() => setStatus(null)}>
                {t('report_bug_error_desc')}
              </Notification>
            )}
          </Stack>
        )}

        {viewMode === 'list' && (
          <Box>
            <Group justify="space-between" mb="md">
              <Text size="sm" c="dimmed" style={{ maxWidth: 720 }}>
                {isAdmin 
                  ? "Jako Administrator widzisz wszystkie zgłoszenia z całego systemu. Zmiana statusu na Rozwiązany (RESOLVED) wyśle automatycznego e-maila po angielsku do zgłaszającego i do wszystkich powiązanych duplikatów!"
                  : "Tutaj widzisz historię swoich zgłoszeń oraz w czasie rzeczywistym podglądasz stan prac naszego zespołu programistycznego."}
              </Text>
              <Button size="xs" variant="light" color="gray" leftSection={<IconRefresh size={14}/>} onClick={fetchTickets} loading={fetching}>
                Odśwież listę
              </Button>
            </Group>

            {Array.isArray(tickets) && tickets.length > 0 ? (
              <Stack gap="md">
                {tickets.map(b => (
                  <Paper key={b.id} withBorder p="md" radius="md" style={{ borderLeft: b.status === 'RESOLVED' ? '5px solid #22c55e' : b.status === 'IN_PROGRESS' ? '5px solid #f59e0b' : b.status === 'DUPLICATE' ? '5px solid #8b5cf6' : '5px solid #ef4444', background: b.status === 'RESOLVED' ? '#f0fdf4' : '#ffffff' }}>
                    <Group justify="space-between" mb="xs">
                      <Group gap="sm">
                        <Badge color="dark" variant="filled">#{b.id}</Badge>
                        <Text fw={700} size="md" c="dark.9">{b.title || 'Zgłoszenie problemu technicznego'}</Text>
                      </Group>
                      <Group gap="xs">
                        <Badge
                          color={b.status === 'RESOLVED' ? 'green' : b.status === 'IN_PROGRESS' ? 'yellow' : b.status === 'DUPLICATE' ? 'violet' : 'red'}
                          variant="filled"
                          size="md"
                        >
                          {b.status === 'RESOLVED' ? t('bug_status_resolved', '✅ Rozwiązany') : b.status === 'IN_PROGRESS' ? t('bug_status_in_progress', '⏳ W trakcie naprawy') : b.status === 'DUPLICATE' ? `${t('bug_status_duplicate', '🔗 Duplikat (AI)')} #${b.duplicateOfId || ''}` : t('bug_status_open', '🔴 Otwarty (Nowy)')}
                        </Badge>
                      </Group>
                    </Group>

                    <Text size="xs" c="dimmed" mb="xs">{t('bug_reporter_label', 'Zgłaszający / Konto:')} <Text span fw={700}>{b.reporterEmail}</Text> | Data: {b.createdAt ? new Date(b.createdAt).toLocaleString() : 'N/A'}</Text>

                    <Box p="sm" mb="sm" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{b.description}</Text>
                    </Box>

                    {b.aiAnalysis && (
                      <Group gap="xs" p="xs" mb="sm" style={{ background: '#f3e8ff', borderRadius: 6, border: '1px solid #d8b4fe', alignItems: 'flex-start' }}>
                        <IconRobot size={18} color="#7e22ce" style={{ flexShrink: 0, marginTop: 2 }} />
                        <Box style={{ flex: 1 }}>
                          <Text size="xs" fw={700} c="purple.9">{t('bug_ai_diagnosis', '🤖 Diagnoza & Analiza AI (Triage):')}</Text>
                          <Text size="xs" c="purple.9">{b.aiAnalysis}</Text>
                        </Box>
                      </Group>
                    )}

                    {isAdmin && (
                      <Box mt="md" p="xs" style={{ background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
                        <Text size="xs" fw={700} c="red.9" mb="xs">🛠️ PANEL ADMINISTRATORA: ZARZĄDZANIE STATUSEM PROBLEMÓW</Text>
                        <Group gap="xs">
                          <Button size="xs" color="yellow" variant={b.status === 'IN_PROGRESS' ? 'filled' : 'light'} onClick={() => handleStatusChange(b.id, 'IN_PROGRESS')}>
                            {t('bug_admin_btn_progress', '⏳ Pracuję nad tym (In Progress)')}
                          </Button>
                          <Button size="xs" color="green" variant={b.status === 'RESOLVED' ? 'filled' : 'light'} onClick={() => handleStatusChange(b.id, 'RESOLVED')}>
                            {t('bug_admin_btn_resolve', '✅ Zrobione! (Oznacz Rozwiązane i wyślij e-mail EN)')}
                          </Button>
                          <Button size="xs" color="gray" variant="subtle" leftSection={<IconTrash size={14}/>} onClick={() => handleDeleteTicket(b.id)}>
                            {t('bug_admin_btn_delete', 'Usuń')}
                          </Button>
                        </Group>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Flex h={240} style={{ border: '2px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc' }} align="center" justify="center" direction="column" c="dimmed">
                <IconBug size={48} stroke={1.2} color="#94a3b8"/>
                <Text mt="md" fw={500}>{t('bug_no_tickets', 'Brak zewidencjonowanych błędów. Dziękujemy, że pomagasz udoskonalać system!')}</Text>
              </Flex>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
}
